import React, { useEffect, useState } from 'react';
import {
    Drawer,
    List,
    Tooltip,
    Typography,
    Box,
    Button,
    Grid,
    ListItemIcon,
    Stack,
} from "@mui/material";
import { useHistory } from "react-router-dom";
import { useGetCartQuery, useGetUserInfoQuery } from "../../../services/grace";
import CartItemDialog from "../../dialogs/CartItemDialog/CartItemDialog";
import ChangePasswordDialog from "../../dialogs/ChangePasswordDialog/ChangePasswordDialog";
import { useAlertDispatch } from '../../../context/AlertContext/AlertContext';
import StyledButton from "../StyledButton/StyledButton";
import AppDrawerButton from "../AppDrawerButton/AppDrawerButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import logo from '../../../assets/grace_information-logo-red.svg';
import gatewayLogo from '../../../assets/gateway-logo-red.svg';

enum AppModes {
    Normal = 'normal',
    AddMaterials = 'addMaterials',
    AddServices = 'addServices',
    ChangePassword = 'changePassword'
}

const addIcon = (<ListItemIcon sx={{ justifyContent: 'flex-end' }}>
    <StyledButton color="secondary" variant="contained" size="small" startIcon={ 
        <FontAwesomeIcon size="sm" icon={['far', 'plus']}  /> 
    }>
        Add
    </StyledButton>
</ListItemIcon>)

const redirectIcon = (<ListItemIcon sx={{ justifyContent: 'flex-end' }}>
    <FontAwesomeIcon  icon={['fas', 'up-right-from-square']}  /> 
</ListItemIcon>)

declare type AppMode = AppModes.Normal | AppModes.AddMaterials | AppModes.AddServices | AppModes.ChangePassword;

// Get today's date
const today = new Date();

// Format the date
const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'long', year: 'numeric' };
const formattedDate = today.toLocaleDateString('en-GB', options);

// Format the greeting
var hours = today.getHours();
let message: string;
if (hours < 12) {
    message = "Good morning";
} else if (hours < 18) {
    message = "Good afternoon";
} else {
    message = "Good evening";
}

export default function AppDrawerHome(): JSX.Element {
    const history = useHistory();
    const alertDispatch = useAlertDispatch();

    const [forcePasswordChange, setForcePasswordChange] = useState(false);

    // Errors for userData are caught in App.tsx
    const {
        data: userData,
        refetch: refetchUserData,
    } = useGetUserInfoQuery();

    const [loginText, setLoginText] = useState("");

    useEffect(() => {
        setLoginText(
            (userData !== undefined && userData.lastLogin !== undefined)
                ? `Last login: ${userData?.lastLogin}`
                : ""
        );

        if (userData !== undefined && userData.pwChangeRequired) {
            setAppMode(AppModes.ChangePassword)
            setForcePasswordChange(true);
        }
    }, [userData])

    // Only load the cart if the user has access to it
    const loadCart = userData === undefined ? false : (
        userData.permissions.orderItems
        || userData.permissions.orderMaterials
        || userData.permissions.orderServices
    );

    // Catch cart data load issues in this file
    const {
        data: cartData,
        error: cartError,
        isFetching: cartIsFetching,
        isError: cartIsError,
    } = useGetCartQuery(null, { skip: !loadCart });

    const [cartBadge, setCartBadge] = useState("");

    useEffect(() => {
        if (cartIsError) {
            setCartBadge("!");
            alertDispatch({
                type: 'add',
                payload: {
                    message: "Failed to load cart data",
                }
            })
        } else if (cartIsFetching || cartData === undefined) {
            setCartBadge("?");
        } else {
            setCartBadge(String(cartData.length));
        }
    }, [cartData, cartIsFetching, cartIsError, cartError, alertDispatch])

    const [appMode, setAppMode] = useState<AppMode>(AppModes.Normal);

    const handleItemClick = (event: React.MouseEvent<HTMLElement>) => {
        switch (event.currentTarget.dataset.index) {
            case "logo":
            case "home": {
                if (history.location.pathname !== "/") {
                    history.push("/");
                }
                break;
            }

            case "inventory":
            case "search":
            case "cart":
            case "order":
            case "addresses":
            case "logout": {
                if (history.location.pathname !== `/${event.currentTarget.dataset.index}`) {
                    history.push(`/${event.currentTarget.dataset.index}`);
                }
                break;
            }

            case "materials": {
                setAppMode(AppModes.AddMaterials);
                break;
            }

            case "services": {
                setAppMode(AppModes.AddServices);
                break;
            }

            case "changePassword": {
                setAppMode(AppModes.ChangePassword);
                break;
            }

            case "howtovideos": {
                window.open(`https://link.grace.com.au/gateway-how-to-videos`);
                break;
            }

            case "userguide": {
                window.open(`https://link.grace.com.au/gateway-user-guide`);
                break;
            }

            case "priceguide": {
                window.open(`https://link.grace.com.au/gateway-price-guide`);
                break;
            }

            case "exporter":
            case "sod":
            case "safepath":
            case "analytix": {
                window.open(`/api/services/${event.currentTarget.dataset.index}`);
                break;
            }
        }
    }

    const handleAddMaterialsClose = () => setAppMode(AppModes.Normal);

    const handleAddMaterialsSubmit = () => setAppMode(AppModes.Normal);

    const handleAddServicesClose = () => setAppMode(AppModes.Normal);

    const handleAddServicesSubmit = () => setAppMode(AppModes.Normal);

    const handleChangePasswordClose = () => setAppMode(AppModes.Normal);

    const handleInventorySearch = () => history.push("/search"); 

    const handleChangePasswordSubmit = () => {
        setForcePasswordChange(false);
        setAppMode(AppModes.Normal);
        // Force a refetch of user data to ensure
        // the password change flag is reset
        refetchUserData();
    }

    return (
        <Box>
            <CartItemDialog
                open={appMode === AppModes.AddMaterials}
                type='material'
                mode='add'
                cartItems={[]}
                itemTypes={userData?.materials}
                locationTypes={userData?.gLocations}
                onClose={handleAddMaterialsClose}
                onSubmit={handleAddMaterialsSubmit}
            />

            <CartItemDialog
                open={appMode === AppModes.AddServices}
                type='service'
                mode='add'
                cartItems={[]}
                itemTypes={userData?.services}
                locationTypes={userData?.gLocations}
                onClose={handleAddServicesClose}
                onSubmit={handleAddServicesSubmit}
            />

            <ChangePasswordDialog
                open={appMode === AppModes.ChangePassword}
                preventClose={forcePasswordChange}
                onClose={handleChangePasswordClose}
                onSubmit={handleChangePasswordSubmit}
            />
 
            <Grid container spacing={2}>
                <Grid item xs={12} md={3} >
                    <img data-index="logo" onClick={handleItemClick} src={logo} style={{maxWidth: '145px'}} alt="Grace Logo" />
                </Grid>
                <Grid item xs={12} md={6}>
                    <Stack alignItems="center">
                        <img src={gatewayLogo} style={{maxWidth: '222px'}} alt="Gateway Logo" />
                        <Typography variant="h5" fontWeight="normal" sx={{ marginTop:0 }}>
                            {message}, {userData?.login}
                        </Typography>
                    </Stack>
                </Grid>
                <Grid item xs={12} md={3}>
                    <Stack spacing={2} sx={{width: '252px', float: {md:'right'}}}>
                        <div style={{background: "#F5F5F5", borderRadius: "100px", textAlign: "center", padding: "7px 0px",fontSize: '18px'}}>
                            {formattedDate}
                        </div>
                        <Stack justifyContent="space-between" spacing={1} direction="row">
                            <StyledButton color="secondary" variant="contained" size="large" startIcon={ 
                                <FontAwesomeIcon icon={['far', 'home']} style={{fontSize: '18px', padding: "6px 0px"}}/> 
                            } dataIndex={'home'} onClick={handleItemClick} style={{width: '119px'}}>
                                Home
                            </StyledButton>
                            <StyledButton color="primary"  size="large" startIcon={ 
                                <FontAwesomeIcon icon={['fas', 'sign-out']} style={{fontSize: '18px', padding: "6px 0px"}}  /> 
                            } dataIndex={'logout'} onClick={handleItemClick} style={{width: '119px'}}>
                                Log Out
                            </StyledButton>
                        </Stack>
                    </Stack>
                </Grid>
            </Grid> 
            <Grid container spacing={2} style={{fontWeight: "bold", marginTop: '100px'}}>
                <Grid item xs={12} md={3}>
                    <Typography variant="h3" sx={{textAlign: "center", marginBottom: "10px", lineHeight: '1em'}}>
                        <Button variant="text" 
                            onClick={() => handleInventorySearch()}
                            sx={{background: "#F5F5F5", color: '#000000', width: "100%", borderRadius: "100px", textTransform: "none", padding: '14px 45px', justifyContent: 'left'}}
                            size="large"
                            startIcon={
                                <FontAwesomeIcon icon={['far', 'search']} title='Search' style={{marginRight: '12px'}} />
                            }
                        >
                            Search your inventory
                        </Button> 
                    </Typography>
                    <Typography variant="h3" sx={{textAlign: "center", marginBottom: "10px", lineHeight: '1em'}}>
                        <Button variant="text" 
                            onClick={handleItemClick}
                            sx={{width: "100%", textTransform: "none", padding: '14px 45px', justifyContent: 'left'}}
                            size="large"
                            data-index="howtovideos"
                        >
                            How to Videos
                        </Button> 
                    </Typography>
                    <Typography variant="h3" sx={{textAlign: "center", marginBottom: "10px", lineHeight: '1em'}}>
                        <Button variant="text" 
                            onClick={handleItemClick}
                            sx={{width: "100%", textTransform: "none", padding: '14px 45px', justifyContent: 'left'}}
                            size="large"
                            data-index="userguide"
                        >
                            User Guide
                        </Button> 
                    </Typography>
                    <Typography variant="h3" sx={{textAlign: "center", marginBottom: "10px", lineHeight: '1em'}}>
                        <Button variant="text" 
                            onClick={handleItemClick}
                            sx={{width: "100%", textTransform: "none", padding: '14px 45px', justifyContent: 'left'}}
                            size="large"
                            data-index="priceguide"
                        >
                            Price Guide
                        </Button> 
                    </Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                    <div style={{padding: '14px 15px', background: '#db291d', color: '#FFF' }}>
                        Cart
                    </div>
                    <List component="nav" aria-label="Cart">
                        {(userData?.permissions.orderItems || userData?.permissions.orderMaterials || userData?.permissions.orderServices) &&
                            <AppDrawerButton
                                dataIndex={'cart'}
                                onClick={handleItemClick}
                                text={"View Cart (" + cartBadge + ")"}
                                secondaryAction={redirectIcon} 
                            />
                        }
                        {userData?.permissions.orderMaterials &&
                            <AppDrawerButton dataIndex={'materials'} onClick={handleItemClick} text="Materials" icon={['fas', 'boxes']} secondaryAction={addIcon} />
                        } 
                        {userData?.permissions.orderServices &&
                            <AppDrawerButton dataIndex={'services'} onClick={handleItemClick} text="Services" icon={['fas', 'person-carry']} secondaryAction={addIcon} />
                        }
                    </List>
                </Grid>
                <Grid item xs={12} md={3}>
                    <div style={{padding: '14px 15px', background: '#aeb3b6', color: '#FFF' }}>
                        Go to
                    </div>
                    <List component="nav" aria-label="Go to">
                        <AppDrawerButton dataIndex={'exporter'} onClick={handleItemClick} text="File Exporter" icon={['fas', 'file-export']} secondaryAction={redirectIcon} />
                        <AppDrawerButton dataIndex={'sod'} onClick={handleItemClick} text="Scan on Demand" icon={['fas', 'scanner-image']} secondaryAction={redirectIcon} />
                        <AppDrawerButton dataIndex={'safepath'} onClick={handleItemClick} text="SafePath" secondaryAction={redirectIcon} />
                        <AppDrawerButton dataIndex={'analytix'} onClick={handleItemClick} text="AnalytiX" secondaryAction={redirectIcon} />
                    </List>
                </Grid>
                <Grid item xs={12} md={3}>
                    <div style={{padding: '14px 15px', background: '#344048', color: '#FFF' }}>
                        Account
                    </div>
                    <List component="nav" aria-label="Account">
                        {userData?.permissions.search &&
                            <AppDrawerButton dataIndex={'inventory'} onClick={handleItemClick} text="Inventory" icon={['fas', 'warehouse-alt']} secondaryAction={redirectIcon} />
                        }
                        {userData?.permissions.orderStatus &&
                            <AppDrawerButton dataIndex={'order'} onClick={handleItemClick} text="Orders" icon={['fas', 'forklift']} secondaryAction={redirectIcon} />
                        }
                        {userData?.permissions.manageAddresses && true &&
                            <AppDrawerButton dataIndex={'addresses'} onClick={handleItemClick} text="Addresses" icon={['fas', 'address-book']} secondaryAction={redirectIcon} />
                        }
                        {userData?.permissions.pwChange &&
                            <AppDrawerButton dataIndex={'changePassword'} onClick={handleItemClick} text="Change Password" icon={['fas', 'key']} secondaryAction={redirectIcon}/>
                        }
                    </List>
                </Grid>
            </Grid>
        </Box>
    );
}
